import { useState } from 'react';
import { IconBook2, IconClock, IconDownload, IconExternalLink, IconPlayerPlay } from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useT } from '@hana/i18n';
import '../edu.i18n';
import { Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast } from '@hana/ui';
import { CERTS, COURSES, SUGGESTIONS, type Course } from '../data/courses';
import { ctaOf, dueSoon, dueTone, learning, mySoonCerts, openRequired, overdueCount, pctLabel, progress, ring } from '../data/logic';
import { useEdu } from '../useEdu';
import { useCourseDrawer } from '../courses/CourseDrawer';
import { useEduDock } from '../dock/EduDock';
import { HomeCompact } from './HomeCompact';
import s from '../edu.module.css';

// Màn Trang chủ (người học) của HANA Edu — board Mint-Training, app `home`.
// TODO(B-10b): KPI / thẻ khoá học mở màn Khoá học + ngăn kéo bài học khi các màn đó xong.

const GOAL_TICKS = 24;
const ON_TIME = 0.86;
const ON_TIME_GOAL = 0.9;

const Path = ({ d, size = 19 }: { d: string; size?: number }) => (
  <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);

function Ring({ p, color, label, size, text, fontSize }: { p: number; color: string; label?: string; size: number; text: string; fontSize: number }) {
  return (
    <svg {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })} width={size} height={size} viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="18" fill="none" stroke="var(--line)" strokeWidth="6" />
      <circle cx="24" cy="24" r="18" fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeDasharray={ring(p)} transform="rotate(-90 24 24)" />
      <text x="24" y={fontSize > 12 ? 28 : 27.5} textAnchor="middle" fontSize={fontSize} fontWeight="600" fill="var(--tx)" fontFamily="Inter, sans-serif">{text}</text>
    </svg>
  );
}

const barColor = (c: Course) => (c.passed ? 'var(--ok-dot)' : c.late ? 'var(--d-amber)' : 'var(--d-blue)');

function HomeBody({ viewer, seat, open }: { viewer: string; seat: string; open: (c: Course) => void }) {
  const t = useT();
  const toast = useToast();
  const added = useState<Record<number, boolean>>({});
  const [on, setOn] = added;
  const mine = learning();
  const due = dueSoon();
  const sugs = SUGGESTIONS[viewer as 'DAVID'] ?? SUGGESTIONS.DAVID;
  const soon = mySoonCerts(viewer, CERTS);
  const certN = soon.length;
  const goalAt = Math.round(ON_TIME_GOAL * GOAL_TICKS);
  const kpis = [
    { k: 'Bắt buộc đang mở', v: String(openRequired()), u: 'khoá', tone: '', tip: 'Khoá bắt buộc theo ghế chưa xong' },
    { k: 'Quá hạn', v: String(overdueCount()), u: 'khoá', tone: 'err', tip: 'Quá hạn task TRN' },
    { k: 'Đúng hạn', v: `${Math.round(ON_TIME * 100)}%`, u: 'quý 4', tone: '', tip: 'Hoàn thành đúng hạn quý 4 · mục tiêu 90%', bar: true },
    { k: 'Chứng chỉ sắp hết hạn', v: String(certN), u: '≤ 30 ngày', tone: certN ? 'warn' : '', tip: 'Hết hạn trong 30 ngày · đã sinh task CRT' },
  ];

  return (
    <div className={s.page}>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <button key={k.k} type="button" className={`${s.card} ${s.kpi}`} title={k.tip} onClick={() => toast({ msg: k.tip })}>
            <div className={s.kpiK}>{k.k}</div>
            <div className={s.kpiV}><b data-tone={k.tone}>{k.v}</b><span>{k.u}</span></div>
            {k.bar && (
              <div className={s.ticks} role="img" aria-label={`Hoàn thành đúng hạn ${Math.round(ON_TIME * 100)}%, mục tiêu ${Math.round(ON_TIME_GOAL * 100)}%`}>
                {Array.from({ length: GOAL_TICKS }, (_, i) => <i key={i} data-on={i < Math.round(ON_TIME * GOAL_TICKS) ? '1' : undefined} data-goal={i === goalAt - 1 && i >= Math.round(ON_TIME * GOAL_TICKS) ? '1' : undefined} />)}
              </div>
            )}
          </button>
        ))}
      </div>

      <section className={`${s.card} ${s.learn}`} aria-label="Đang học">
        <div className={s.learnHead}><h2 className={s.h2}>{t('ed.learning')}</h2><span>{mine.length}</span></div>
        <div className={s.learnGrid}>
          {mine.map((c) => {
            const cta = ctaOf(c);
            return (
              <button key={c.id} type="button" className={s.lc} aria-label={`${c.k} · ${c.done}/${c.total} bài · ${cta}`} onClick={() => open(c)}>
                <Ring p={progress(c)} color={barColor(c)} size={56} text={pctLabel(c)} fontSize={9.5} />
                <div className={s.lcBody}>
                  <b>{c.short}</b>
                  <div className={s.lcMeta}>
                    <span><IconBook2 size={14} aria-hidden="true" />{c.done}/{c.total}</span>
                    <span className={s.pill} data-tone={c.done === c.total ? 'go' : c.late ? 'err' : undefined}>{cta}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <div className={s.cols}>
        <section className={`${s.card} ${s.col}`} aria-label="Bắt buộc sắp hạn">
          <div className={s.colHead}>
            <h2 className={s.h2}>{t('ed.dueSoon')}</h2>
            <span className={s.info} title="Số trong ô = ngày làm việc còn lại tới hạn · đỏ là đã quá hạn · mỗi khoá là một task TRN" aria-label="Chú thích">i</span>
          </div>
          {due.map((c) => (
            <button key={c.id} type="button" className={s.row} onClick={() => open(c)}>
              <span className={s.dueBox} data-tone={dueTone(c.dd!)} title={`Hạn ${c.due} · ${c.dueSub}`}>
                <b>{c.dd}</b>
                <span>{c.dd! < 0 ? 'trễ' : 'ngày'}</span>
              </span>
              <div className={s.rowMain}>
                <b>{c.short}</b>
                <div className={s.bar}><i style={{ width: pctLabel(c) }} data-late={c.late ? '1' : undefined} /></div>
              </div>
              <span className={s.rowPct}>{pctLabel(c)}</span>
            </button>
          ))}
        </section>

        <section className={`${s.card} ${s.col}`} aria-label="Gợi ý theo ghế">
          <div className={s.colHead}><h2 className={s.h2}>{t('ed.forSeat')}</h2><span className={s.seatTag}>{seat}</span></div>
          {sugs.map((g, i) => {
            const picked = !!on[i];
            const label = `${picked ? 'Đã thêm vào' : 'Thêm vào'} Khoá học: ${g.k}`;
            return (
              <div key={g.k} className={s.row}>
                <span className={s.sugIc}><Path d={g.icon} /></span>
                <b className={s.sugName}>{g.k}</b>
                <span className={s.sugMin} title="Thời lượng"><IconClock size={14} aria-hidden="true" />{g.minutes}'</span>
                <button
                  type="button"
                  className={s.sugAdd}
                  aria-pressed={picked}
                  aria-label={label}
                  title={label}
                  onClick={() => {
                    setOn({ ...on, [i]: !picked });
                    toast({ msg: `${picked ? 'Đã bỏ khỏi' : 'Đã thêm vào'} Khoá học: ${g.k}` });
                  }}
                >{picked ? '✓' : '+'}</button>
              </div>
            );
          })}
        </section>

        <section className={`${s.card} ${s.col} ${s.cert}`} aria-label="Chứng chỉ sắp hết hạn">
          <h2 className={s.h2}>{t('ed.certsSoon')}</h2>
          {soon.map((c) => (
            <div key={c.k}>
              <div className={s.certRow}>
                <Ring p={Math.min(1, (c.days ?? 0) / 30)} color={c.status === 'exp' ? 'var(--err)' : 'var(--warn)'} size={64} text={String(c.days)} fontSize={13} label={`Còn ${c.days} ngày tới hạn ${c.expires}`} />
                <div className={s.certBody}>
                  <b>{c.k}</b>
                  <span data-tone={c.status === 'exp' ? 'err' : undefined}>{c.expires}</span>
                </div>
              </div>
              <button type="button" className={s.renew} onClick={() => toast({ msg: t('ed.renewed') })}><IconDownload size={16} aria-hidden="true" />{t('ed.renew')}</button>
            </div>
          ))}
          {!soon.length && <div className={s.allOk}><span>✓</span>{t('ed.allValid')}</div>}
        </section>
      </div>
    </div>
  );
}

export function HomeScreen() {
  const t = useT();
  const { learner, state } = useEdu();
  const layout = useLayout();
  const phone = layout === 'phone';
  const compact = phone || layout === 'pad';
  const cd = useCourseDrawer();
  const nav = useNavigate();
  const toast = useToast();
  const [density, setDensity] = useState<'std' | 'compact'>('std');
  const dk = useEduDock(learner.manager, !phone && state === 'ready');
  return (
    <Page kind="table" dock={dk.dock}>
      <PageHeader
        title="Trang chủ"
        actions={
          <>
            {!phone && <Segmented kind="toggle" label="Mật độ" value={density} onChange={setDensity} items={[{ key: 'std', label: 'Chuẩn' }, { key: 'compact', label: 'Gọn' }]} />}
            {!phone && dk.tools}
            {state === 'ready' && (phone
              ? <Button variant="primary" icon={<IconPlayerPlay size={19} />} aria-label={t('ed.continue')} onClick={() => cd.open(COURSES[0]!)} />
              : <Button variant="primary" onClick={() => cd.open(COURSES[0]!)}>{t('ed.continue')}</Button>)}
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Chưa có khoá học nào được giao' : state === 'error' ? 'Không tải được dữ liệu đào tạo' : undefined}
        desc={state === 'empty' ? 'Chưa có khoá bắt buộc.' : state === 'error' ? 'Mất kết nối lúc 08:42. Tiến độ vẫn lưu trên máy.' : undefined}
        action={
          state === 'empty' ? (
            <Button variant="primary" icon={<IconExternalLink size={16} />} onClick={() => void nav({ to: '/edu/library' })}>{t('ed.openLib')}</Button>
          ) : state === 'error' ? (
            <Button icon={<IconPlayerPlay size={16} />} onClick={() => toast({ msg: t('ed.reconnecting') })}>{t('ed.retry')}</Button>
          ) : undefined
        }
        skeleton={<div className={s.skel}><Skeleton rows={4} /><Skeleton rows={4} /></div>}
      >
        {compact ? <HomeCompact viewer={learner.k} seat={learner.seat} open={cd.open} phone={phone} /> : <HomeBody viewer={learner.k} seat={learner.seat} open={cd.open} />}
      </StateView>
      {cd.node}
    </Page>
  );
}
